import {
  AlertTriangle,
  Building,
  Car,
  HardHat,
  Heart,
  HelpCircle,
  Shield,
  Users,
} from 'lucide-react';

import {
  BenefitType,
  CoverageCategory,
  IdentifierType,
  PolicyType,
} from '@/services/types/coverage-types';

export const categoryConfig = {
  [CoverageCategory.FIRST_PARTY]: {
    label: 'First Party',
    icon: Car,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    description: "Client's auto coverage",
  },
  [CoverageCategory.FIRST_PARTY_HEALTH]: {
    label: 'Health Insurance',
    icon: Heart,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    description: "Client's health insurance",
  },
  [CoverageCategory.THIRD_PARTY]: {
    label: 'Third Party',
    icon: Shield,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    description: "At-fault party's coverage",
  },
  [CoverageCategory.NOT_LIABLE_INVOLVED_PARTY]: {
    label: 'Not-Liable Involved Party',
    icon: Users,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'Coverage for involved but non-fault parties',
  },
  [CoverageCategory.UNCLEAR]: {
    label: 'Unclear Coverage',
    icon: HelpCircle,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    description: 'Coverage needs classification',
  },
  [CoverageCategory.PROPERTY]: {
    label: 'Property',
    icon: Building,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    description: 'Premises liability property',
  },
  [CoverageCategory.WORKERS_COMPENSATION]: {
    label: 'Workers Compensation',
    icon: HardHat,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    description: 'Workplace injury coverage',
  },
};

// Simplified version for merge components
export const categoryConfigSimple = {
  [CoverageCategory.FIRST_PARTY]: {
    label: 'First Party',
    icon: Car,
    color: 'text-blue-600',
  },
  [CoverageCategory.FIRST_PARTY_HEALTH]: {
    label: 'Health Insurance',
    icon: Heart,
    color: 'text-green-600',
  },
  [CoverageCategory.THIRD_PARTY]: {
    label: 'Third Party',
    icon: Shield,
    color: 'text-purple-600',
  },
  [CoverageCategory.NOT_LIABLE_INVOLVED_PARTY]: {
    label: 'Not-Liable Involved Party',
    icon: Users,
    color: 'text-amber-600',
  },
  [CoverageCategory.UNCLEAR]: {
    label: 'Unclear',
    icon: AlertTriangle,
    color: 'text-gray-600',
  },
  [CoverageCategory.PROPERTY]: {
    label: 'Property',
    icon: Building,
    color: 'text-orange-600',
  },
  [CoverageCategory.WORKERS_COMPENSATION]: {
    label: 'Workers Compensation',
    icon: HardHat,
    color: 'text-yellow-600',
  },
};

// Category badge configurations for cards
export const categoryBadgeConfig = {
  [CoverageCategory.FIRST_PARTY]: {
    text: '1P',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  [CoverageCategory.FIRST_PARTY_HEALTH]: {
    text: '1P',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  [CoverageCategory.THIRD_PARTY]: {
    text: '3P',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
  },
  [CoverageCategory.NOT_LIABLE_INVOLVED_PARTY]: {
    text: 'NLI',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  [CoverageCategory.UNCLEAR]: {
    text: '?',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
  },
  [CoverageCategory.PROPERTY]: {
    text: 'PROP',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
  },
  [CoverageCategory.WORKERS_COMPENSATION]: {
    text: 'WC',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  },
};

export const policyTypeLabels = {
  [PolicyType.AUTO]: 'Auto',
  [PolicyType.HEALTH]: 'Health',
  [PolicyType.HOMEOWNERS]: 'Homeowners',
  [PolicyType.COMMERCIAL]: 'Commercial',
  [PolicyType.UMBRELLA]: 'Umbrella',
  [PolicyType.GENERAL_LIABILITY]: 'General Liability',
  [PolicyType.OTHER]: 'Other',
};

// Extended version for detailed display
export const policyTypeLabelsDetailed = {
  [PolicyType.AUTO]: 'Auto Insurance',
  [PolicyType.HEALTH]: 'Health Insurance',
  [PolicyType.HOMEOWNERS]: 'Homeowners Insurance',
  [PolicyType.COMMERCIAL]: 'Commercial Insurance',
  [PolicyType.UMBRELLA]: 'Umbrella Insurance',
  [PolicyType.GENERAL_LIABILITY]: 'General Liability Insurance',
  [PolicyType.OTHER]: 'Other Insurance',
};

export const categoryLabels = {
  [CoverageCategory.FIRST_PARTY]: 'First Party Coverage',
  [CoverageCategory.FIRST_PARTY_HEALTH]: 'Health Insurance',
  [CoverageCategory.THIRD_PARTY]: 'Third Party Coverage',
  [CoverageCategory.NOT_LIABLE_INVOLVED_PARTY]:
    'Not-Liable Involved Party Coverage',
  [CoverageCategory.UNCLEAR]: 'Unclear Coverage',
  [CoverageCategory.PROPERTY]: 'Properties',
  [CoverageCategory.WORKERS_COMPENSATION]: 'Workers Compensation',
};

export const identifierTypeLabels = {
  [IdentifierType.POLICY_NUMBER]: 'Policy Number',
  [IdentifierType.MEMBER_ID]: 'Member ID',
  [IdentifierType.GROUP_NUMBER]: 'Group Number',
  [IdentifierType.OTHER]: 'Identifier',
};

// Benefit type labels - shared across ClaimsSection and CoverageLimitsSection
export const benefitTypeLabels = {
  [BenefitType.PERSONAL_INJURY_PROTECTION]: 'PIP',
  [BenefitType.MEDICAL_PAYMENTS]: 'MedPay',
  [BenefitType.UNINSURED_MOTORIST]: 'UM',
  [BenefitType.UNDER_INSURED_MOTORIST]: 'UIM',
  [BenefitType.BODILY_INJURY_LIABILITY]: 'BI Liability',
  [BenefitType.PROPERTY_DAMAGE_LIABILITY]: 'PD Liability',
  [BenefitType.COMPREHENSIVE_AUTO]: 'Comprehensive',
  [BenefitType.COLLISION_AUTO]: 'Collision',
  [BenefitType.WORKERS_COMPENSATION]: 'Workers Comp',
  [BenefitType.OTHER]: 'Other',
};

export const benefitTypeLabelsShorthand = {
  [BenefitType.PERSONAL_INJURY_PROTECTION]: 'PIP',
  [BenefitType.MEDICAL_PAYMENTS]: 'MedPay',
  [BenefitType.UNINSURED_MOTORIST]: 'UM',
  [BenefitType.UNDER_INSURED_MOTORIST]: 'UIM',
  [BenefitType.BODILY_INJURY_LIABILITY]: 'BI',
  [BenefitType.PROPERTY_DAMAGE_LIABILITY]: 'PD',
  [BenefitType.COMPREHENSIVE_AUTO]: 'Comprehensive',
  [BenefitType.COLLISION_AUTO]: 'Collision',
  [BenefitType.WORKERS_COMPENSATION]: 'WC',
  [BenefitType.OTHER]: 'Other',
};

// Benefit priority orders for different coverage categories
export const benefitPriorityOrders = {
  [CoverageCategory.FIRST_PARTY]: [
    BenefitType.PERSONAL_INJURY_PROTECTION,
    BenefitType.MEDICAL_PAYMENTS,
    BenefitType.UNINSURED_MOTORIST,
    BenefitType.UNDER_INSURED_MOTORIST,
    BenefitType.COMPREHENSIVE_AUTO,
    BenefitType.COLLISION_AUTO,
    BenefitType.OTHER,
  ],
  [CoverageCategory.FIRST_PARTY_HEALTH]: [
    BenefitType.PERSONAL_INJURY_PROTECTION,
    BenefitType.MEDICAL_PAYMENTS,
    BenefitType.UNINSURED_MOTORIST,
    BenefitType.UNDER_INSURED_MOTORIST,
    BenefitType.COMPREHENSIVE_AUTO,
    BenefitType.COLLISION_AUTO,
    BenefitType.OTHER,
  ],
  [CoverageCategory.THIRD_PARTY]: [
    BenefitType.BODILY_INJURY_LIABILITY,
    BenefitType.PROPERTY_DAMAGE_LIABILITY,
    BenefitType.PERSONAL_INJURY_PROTECTION,
    BenefitType.MEDICAL_PAYMENTS,
    BenefitType.UNINSURED_MOTORIST,
    BenefitType.UNDER_INSURED_MOTORIST,
    BenefitType.OTHER,
  ],
  [CoverageCategory.NOT_LIABLE_INVOLVED_PARTY]: [
    BenefitType.BODILY_INJURY_LIABILITY,
    BenefitType.PROPERTY_DAMAGE_LIABILITY,
    BenefitType.PERSONAL_INJURY_PROTECTION,
    BenefitType.MEDICAL_PAYMENTS,
    BenefitType.UNINSURED_MOTORIST,
    BenefitType.UNDER_INSURED_MOTORIST,
    BenefitType.OTHER,
  ],
  [CoverageCategory.UNCLEAR]: [
    BenefitType.PERSONAL_INJURY_PROTECTION,
    BenefitType.BODILY_INJURY_LIABILITY,
    BenefitType.MEDICAL_PAYMENTS,
    BenefitType.PROPERTY_DAMAGE_LIABILITY,
    BenefitType.UNINSURED_MOTORIST,
    BenefitType.UNDER_INSURED_MOTORIST,
    BenefitType.COMPREHENSIVE_AUTO,
    BenefitType.COLLISION_AUTO,
    BenefitType.OTHER,
  ],
  [CoverageCategory.PROPERTY]: [
    BenefitType.PROPERTY_DAMAGE_LIABILITY,
    BenefitType.BODILY_INJURY_LIABILITY,
    BenefitType.OTHER,
  ],
  [CoverageCategory.WORKERS_COMPENSATION]: [
    BenefitType.MEDICAL_PAYMENTS,
    BenefitType.OTHER,
  ],
};

/**
 * Benefit types relevant for 1P (First Party) coverage cards.
 * For 1P, only UM/UIM/PIP/MedPay apply — BI Liability, PD Liability, etc. do not.
 */
export const FIRST_PARTY_RELEVANT_BENEFIT_TYPES: ReadonlySet<BenefitType> = new Set([
  BenefitType.UNINSURED_MOTORIST,
  BenefitType.UNDER_INSURED_MOTORIST,
  BenefitType.PERSONAL_INJURY_PROTECTION,
  BenefitType.MEDICAL_PAYMENTS,
]);

// Helper function to get benefit priority order for a coverage category
export const getBenefitPriorityOrder = (category: CoverageCategory | null) => {
  return (
    benefitPriorityOrders[category || CoverageCategory.UNCLEAR] ||
    benefitPriorityOrders[CoverageCategory.UNCLEAR]
  );
};
